import { gql, useQuery } from '@apollo/client';

/**
 * Armazenamento duplo (Amazon S3 + DigitalOcean Spaces).
 *
 * O backend grava todo arquivo nos dois buckets usando exatamente a mesma key
 * (mesmo caminho + nome). As URLs salvas no banco continuam apontando para o
 * Amazon S3. Para alternar o provedor de leitura (ex.: failover quando o S3
 * estiver fora do ar) basta trocar o host da URL — é o que este helper faz.
 *
 * O provedor ativo vem de core.storage_provider ('s3' | 'spaces').
 */

const S3_HOST =
  process.env.REACT_APP_S3_HOST || 'drderma.s3.us-east-1.amazonaws.com';
const SPACES_HOST =
  process.env.REACT_APP_SPACES_HOST ||
  'drderma.sfo3.cdn.digitaloceanspaces.com';

// Cache em nível de módulo, para uso fora de componentes React.
let activeProvider = 's3';

export const getActiveStorageProvider = () => activeProvider;
export const setActiveStorageProvider = (provider) => {
  if (provider === 's3' || provider === 'spaces') {
    activeProvider = provider;
  }
};

/**
 * Reescreve uma URL de mídia para o provedor desejado, trocando apenas o host
 * (a key/caminho é idêntica nos dois buckets).
 */
export function rewriteMediaUrl(url, provider = activeProvider) {
  if (!url || typeof url !== 'string') return url;

  if (provider === 'spaces') {
    if (url.includes(S3_HOST)) return url.replace(S3_HOST, SPACES_HOST);
    return url;
  }

  // Padrão: Amazon S3
  if (url.includes(SPACES_HOST)) return url.replace(SPACES_HOST, S3_HOST);
  return url;
}

/** Usa o provedor ativo do cache (não-React). */
export const resolveMediaUrl = (url) => rewriteMediaUrl(url, activeProvider);

/** Aplica o helper a uma lista de URLs (ex.: coluna files array). */
export const resolveMediaUrls = (urls) =>
  Array.isArray(urls) ? urls.map((u) => resolveMediaUrl(u)) : urls;

const GET_STORAGE_PROVIDER_ACTIVE = gql`
  query getStorageProviderActive {
    core {
      id
      storage_provider
    }
  }
`;

/**
 * Hook que lê o provedor ativo e mantém o cache de módulo atualizado.
 * Retorna o provedor e uma função resolve(url) que re-renderiza ao mudar.
 */
export function useStorageProvider() {
  const { data } = useQuery(GET_STORAGE_PROVIDER_ACTIVE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const provider = data?.core?.[0]?.storage_provider || 's3';
  setActiveStorageProvider(provider);

  return {
    provider,
    resolve: (url) => rewriteMediaUrl(url, provider),
    resolveMany: (urls) =>
      Array.isArray(urls) ? urls.map((u) => rewriteMediaUrl(u, provider)) : urls,
  };
}
